import { useEffect, useRef } from 'react';
import { platform } from '@/platform';
import { driftAction, loopDrift } from '../../lib/drift';

const CHECK_MS = 250;

/**
 * A video on a wall tile, held to the shared timeline: its expected position is the time since
 * the item started (on the synced clock), looping. Every check compares where it is with where it
 * should be and trims the difference (see driftAction).
 */
export const SyncedVideo = ({ url, className, startedAtMs, now }) => {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const canSetRate = platform.capabilities?.playbackRate !== false;

    const expected = () => {
      const elapsed = Math.max((now() - startedAtMs) / 1000, 0);
      return video.duration > 0 ? elapsed % video.duration : elapsed;
    };

    const check = () => {
      // Not decodable yet: nothing to measure. It is placed as soon as it is.
      if (video.readyState < 2 || !(video.duration > 0)) return;
      const action = driftAction(
        loopDrift(video.currentTime, expected(), video.duration),
        {
          canSetRate,
        },
      );
      if (action.seekTo) {
        video.currentTime = expected();
        if (canSetRate) video.playbackRate = 1;
      } else if (canSetRate && video.playbackRate !== action.rate) {
        video.playbackRate = action.rate;
      }
      if (video.paused) video.play().catch(() => {});
    };

    // Jump straight to the right spot once it can play, then keep it there.
    video.addEventListener('loadeddata', check);
    const timer = setInterval(check, CHECK_MS);
    return () => {
      clearInterval(timer);
      video.removeEventListener('loadeddata', check);
    };
  }, [url, startedAtMs, now]);

  return (
    <video
      ref={ref}
      src={url}
      className={className}
      crossOrigin="anonymous"
      preload="auto"
      autoPlay
      muted
      loop
      playsInline
      onError={(err) => console.error('playback error', err)}
    />
  );
};
